import React, { useCallback } from 'react'
import AsyncSelect from 'react-select/async'
import { api } from '../services/api'
import { useDebouncedCallback } from '../hooks'
import selectStyles from '../styles/react-select.module.scss'

export interface Complex {
    tc_id: string
    ext_id: string | null
    tc_name: string | null
    ext_name: string | null
}

// Option type extends Complex with react-select required fields
interface ComplexOption extends Complex {
    value: string
    label: string
}

export interface ComplexSelectProps {
    value: Complex | null
    onChange: (complex: Complex | null) => void
    isClearable?: boolean
    placeholder?: string
    className?: string
    isDisabled?: boolean
    isModified?: boolean
}

// Build complex label
const buildComplexLabel = (c: Complex): string => {
    return c.tc_name || c.ext_name || 'Unknown Complex'
}

// Convert Complex to react-select option
const toOption = (c: Complex): ComplexOption => ({
    ...c,
    value: c.tc_id,
    label: buildComplexLabel(c)
})

/**
 * Reusable complex select component with async loading
 */
const ComplexSelect: React.FC<ComplexSelectProps> = ({
    value,
    onChange,
    isClearable = true,
    placeholder = 'Search complexes...',
    className = '',
    isDisabled = false,
    isModified = false
}) => {
    // Transform API response to Complex format
    const transformComplexes = useCallback((rawComplexes: any[]): ComplexOption[] => {
        const complexes: Complex[] = rawComplexes.map((item: any) => ({
            tc_id: item.tc_complex_id,
            ext_id: item.ext_complex_id,
            tc_name: item.tc_name,
            ext_name: item.ext_name,
        }))
        return complexes.map(toOption)
    }, [])

    const fetchComplexesData = useCallback(async (inputValue: string): Promise<ComplexOption[]> => {
        if (inputValue.length < 2) {
            return []
        }

        try {
            const response = await api.get('/complexes', { name: inputValue, limit: 50 })
            const rawComplexes = response?.complexes || []
            return transformComplexes(rawComplexes)
        } catch (error) {
            console.error('Failed to load complexes:', error)
            return []
        }
    }, [transformComplexes])

    const loadComplexes = useDebouncedCallback(fetchComplexesData, 300)

    // Convert value to option format
    const selectValue = value ? toOption(value) : null

    return (
        <div className={isModified ? selectStyles.modified : ''}>
            <AsyncSelect<ComplexOption, false>
                value={selectValue}
                onChange={(option) => onChange(option as Complex | null)}
                loadOptions={loadComplexes}
                defaultOptions={false}
                isClearable={isClearable}
                isDisabled={isDisabled}
                placeholder={placeholder}
                className={`${selectStyles.reactSelect} ${className}`}
                classNamePrefix="react-select"
                cacheOptions
                getOptionValue={(option) => option.tc_id}
                getOptionLabel={(option) => buildComplexLabel(option)}
                noOptionsMessage={({ inputValue }) =>
                    inputValue.length < 2
                        ? "Type at least 2 characters to search..."
                        : "No complexes found"
                }
            />
        </div>
    )
}

export default ComplexSelect

